import { FunctionComponent, JSX, useState } from 'react'
import { TaskNote, TaskNoteStatus, TaskNoteType, TaskNoteUserValue } from '@/types/TaskNote.ts'
import styles from './taskNoteComponent.module.scss'
import { useTranslation } from 'react-i18next'
import { getLocalFromLanguage } from '@/modules/helper/languageHelper.ts'
import { format, parse } from 'date-fns'
import {
    Assignment,
    AssignmentAdd,
    AssignmentTurnedIn,
    Call,
    CallMade,
    Cancel,
    CheckCircle,
    ChildCareOutlined,
    Description,
    EditNote,
    Event,
    HighlightOff,
    HowToReg,
    ListAlt,
    MailOutlined,
    People,
    Person,
    PhoneDisabled,
    VideoCameraFront,
    Voicemail
} from '@mui/icons-material'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import moment from 'moment/moment'
import DeleteTasksNoteDialog from '../Dialog/deleteTasksNoteDialog.tsx'
import EditTasksNotesDialog from '../Dialog/editTasksNotesDialog.tsx'
import { Parent } from '@/types/Parent.ts'
import { Child } from '@/types/Child.ts'
import { StaffWithLinks } from '@/types/Staff.ts'
import MessageWithURL from '@/components/_custom/MessageWithUrl'
import { useDaycareMailTo } from '@/hooks/useDaycareMailTo.ts'

interface TaskNoteComponentProps {
    taskNote: TaskNote
    isParent: boolean
    isRpeOrLaep: boolean
    canUpdate: boolean
    daycareId: number
    handleRefetch: () => void
    parents?: Parent[]
    childs?: Child[]
    staffs?: StaffWithLinks[]
}

const TaskNoteComponent: FunctionComponent<TaskNoteComponentProps> = ({
    taskNote,
    isParent,
    isRpeOrLaep,
    canUpdate,
    daycareId,
    handleRefetch,
    childs,
    parents,
    staffs
}: TaskNoteComponentProps) => {
    const { t } = useTranslation()

    const navigate: NavigateFunction = useNavigate()

    const [isTaskNoteDeleteDialogOpen, setTaskNoteDeleteDialogOpen] = useState<boolean>(false)
    const [isTaskNoteEditDialogOpen, setTaskNoteEditDialogOpen] = useState<boolean>(false)
    const { canContactDaycare, contactDaycare } = useDaycareMailTo()

    return (
        <div className={`${isParent && styles.TaskNotePlacementParent} ${styles.TaskNotePlacement}`}>
            <div className={styles.TitleContainer}>
                {defineTaskNoteTypeIcon(taskNote.type, isParent)}
                <div className={styles.TextContainer}>
                    <p className={styles.Title}>{taskNote.title}</p>
                </div>
            </div>
            {(taskNote.status || taskNote.date) && <div className={styles.BreakLine}></div>}
            {taskNote.status && (
                <div className={styles.Container}>
                    {defineTaskNoteStatusIcon(taskNote.status, taskNote.type, isParent)}
                    <p className={styles.Text}>{t(taskNote.status)}</p>
                </div>
            )}
            {taskNote.date && (
                <div className={styles.Container}>
                    <Event
                        className={`${showDateInRed(taskNote.date, taskNote.status) ? styles.IconDanger : ''} ${isParent ? styles.IconParent : styles.IconDaycare}`}
                    />
                    <p
                        className={`${showDateInRed(taskNote.date, taskNote.status) ? styles.TextDanger : ''} ${styles.Text}`}
                    >
                        {format(parse(taskNote.date, 'dd-MM-yyyy', new Date()), 'EEEE dd MMMM yyyy', {
                            locale: getLocalFromLanguage()
                        })}
                        {taskNote.startHour && (
                            <>
                                {' ' +
                                    t(taskNote.endHour ? 'from' : 'to').toLowerCase() +
                                    ' ' +
                                    format(parse(taskNote.startHour, 'HH:mm:ss', new Date()), 'HH:mm')}
                            </>
                        )}
                        {taskNote.endHour && (
                            <>
                                {' ' +
                                    t('to').toLowerCase() +
                                    ' ' +
                                    format(parse(taskNote.endHour, 'HH:mm:ss', new Date()), 'HH:mm')}
                            </>
                        )}
                    </p>
                </div>
            )}
            {taskNote.description && (
                <>
                    <div className={styles.BreakLine}></div>
                    <div className={styles.Container}>
                        <Description className={isParent ? styles.IconParent : styles.IconDaycare} />
                        <MessageWithURL message={taskNote.description} />
                    </div>
                </>
            )}
            {!isParent && taskNote.internalNote && (
                <>
                    <div className={styles.BreakLine}></div>
                    <div className={styles.Container}>
                        <HowToReg className={isParent ? styles.IconParent : styles.IconDaycare} />
                        <p className={styles.Text}>
                            {taskNote.childIds.length === 0 && haveOnlyStaffs(taskNote.userIds)
                                ? t('visibleByStaff')
                                : isRpeOrLaep
                                  ? t('visibleByPublic')
                                  : t('visibleByParent')}
                        </p>
                    </div>
                </>
            )}
            {(taskNote.childIds.length > 0 || taskNote.userIds.length > 0) && (
                <>
                    <div className={styles.BreakLine} />
                    {taskNote.childIds.length > 0 &&
                        taskNote.childIds.map(
                            (child: TaskNoteUserValue): JSX.Element => (
                                <div key={child.id.toString() + '-child'} className={styles.Container}>
                                    <ChildCareOutlined
                                        className={isParent ? styles.IconParent : styles.ClickableIconDaycare}
                                        onClick={!isParent ? () => navigate(`/childs?child=${child.id}`) : undefined}
                                    />
                                    <p className={styles.Text}>{child.name}</p>
                                </div>
                            )
                        )}
                    {taskNote.userIds.length > 0 &&
                        taskNote.userIds.map(
                            (user: TaskNoteUserValue): JSX.Element => (
                                <div
                                    key={`
                                        ${user.id.toString()}-${
                                            user.isParent === true
                                                ? 'parent'
                                                : user.isParent === false
                                                  ? 'staff'
                                                  : 'error'
                                        }`}
                                    className={styles.Container}
                                >
                                    <Person
                                        className={isParent ? styles.IconParent : styles.ClickableIconDaycare}
                                        onClick={
                                            !isParent
                                                ? () =>
                                                      navigate(
                                                          user.isParent
                                                              ? `/adults?parent=${user.id}`
                                                              : `/staff?profile=${user.id}`
                                                      )
                                                : undefined
                                        }
                                    />
                                    <p className={styles.Text}>{user.name}</p>
                                </div>
                            )
                        )}
                </>
            )}
            {canUpdate && (
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon
                        type={'edit'}
                        className={styles.Button}
                        onClick={() => setTaskNoteEditDialogOpen(true)}
                    />
                    <ButtonTextIcon
                        type={'delete'}
                        className={styles.Button}
                        onClick={() => setTaskNoteDeleteDialogOpen(true)}
                    />
                </div>
            )}
            {canUpdate && isTaskNoteDeleteDialogOpen && (
                <DeleteTasksNoteDialog
                    open={isTaskNoteDeleteDialogOpen}
                    handleClose={() => setTaskNoteDeleteDialogOpen(false)}
                    daycareId={daycareId}
                    taskNoteId={taskNote.id}
                    handleRefetch={handleRefetch}
                />
            )}
            {canUpdate && isTaskNoteEditDialogOpen && (
                <EditTasksNotesDialog
                    open={isTaskNoteEditDialogOpen}
                    handleClose={() => setTaskNoteEditDialogOpen(false)}
                    daycareId={daycareId}
                    handleRefetch={handleRefetch}
                    isRpeOrLaep={isRpeOrLaep}
                    taskNote={taskNote}
                    staffs={staffs}
                    childs={childs}
                    parents={parents}
                />
            )}
            {isParent && canContactDaycare && (
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon
                        type={'custom'}
                        className={styles.Button}
                        customText={t('contactMyDaycare')}
                        customIcon={<MailOutlined />}
                        onClick={() => contactDaycare(`Réponse à ${taskNote.title}`)}
                        parent={isParent}
                    />
                </div>
            )}
        </div>
    )
}

export default TaskNoteComponent

function defineTaskNoteTypeIcon(type: TaskNoteType, isParent: boolean): JSX.Element {
    switch (type) {
        case TaskNoteType.task:
            return <ListAlt className={isParent ? styles.IconParent : styles.IconDaycare} />
        case TaskNoteType.appointmentInPerson:
            return <People className={isParent ? styles.IconParent : styles.IconDaycare} />
        case TaskNoteType.appointmentVideoconference:
            return <VideoCameraFront className={isParent ? styles.IconParent : styles.IconDaycare} />
        case TaskNoteType.note:
            return <EditNote className={isParent ? styles.IconParent : styles.IconDaycare} />
        case TaskNoteType.call:
            return <Call className={isParent ? styles.IconParent : styles.IconDaycare} />
        default:
            return <ListAlt className={isParent ? styles.IconParent : styles.IconDaycare} />
    }
}

function defineTaskNoteStatusIcon(status: TaskNoteStatus, type: TaskNoteType, isParent: boolean): JSX.Element {
    switch (status) {
        case TaskNoteStatus.done:
            if (type === TaskNoteType.appointmentInPerson || type === TaskNoteType.appointmentVideoconference) {
                return <CheckCircle className={isParent ? styles.IconParent : styles.IconDaycare} />
            }
            return <AssignmentTurnedIn className={isParent ? styles.IconParent : styles.IconDaycare} />
        case TaskNoteStatus.inProgress:
            return <Assignment className={isParent ? styles.IconParent : styles.IconDaycare} />
        case TaskNoteStatus.todo:
            return <AssignmentAdd className={isParent ? styles.IconParent : styles.IconDaycare} />
        case TaskNoteStatus.leftMessage:
            return <Voicemail className={isParent ? styles.IconParent : styles.IconDaycare} />
        case TaskNoteStatus.noAnswer:
            return <PhoneDisabled className={isParent ? styles.IconParent : styles.IconDaycare} />
        case TaskNoteStatus.connected:
            return <CallMade className={isParent ? styles.IconParent : styles.IconDaycare} />
        case TaskNoteStatus.cancelled:
            return <Cancel className={isParent ? styles.IconParent : styles.IconDaycare} />
        case TaskNoteStatus.missed:
            return <HighlightOff className={isParent ? styles.IconParent : styles.IconDaycare} />
        case TaskNoteStatus.schedule:
            return <Event className={isParent ? styles.IconParent : styles.IconDaycare} />
        default:
            return <Assignment className={isParent ? styles.IconParent : styles.IconDaycare} />
    }
}

function showDateInRed(date: string, status: TaskNoteStatus | undefined): boolean {
    return (
        (status === TaskNoteStatus.todo || status === TaskNoteStatus.inProgress) &&
        moment(date, 'DD-MM-YYYY').isBefore(moment())
    )
}

function haveOnlyStaffs(userIds: TaskNoteUserValue[]): boolean {
    if (userIds.length === 0) return false

    const staffIds: number[] = []

    userIds.forEach((user: TaskNoteUserValue): void => {
        if (user.isParent === false) staffIds.push(user.id)
    })

    return staffIds.length === userIds.length
}
